import { useState, useEffect } from 'react';

import getConfig from '../../util/config';
import useIsNativeWebView from '../native-bridge/hooks/useIsWebView';

/**
 * Resolves the recaptcha api of a grecaptcha instance. Enterprise deployments
 * nest it under `grecaptcha.enterprise`, the classic v3 script (which is what
 * `react-google-recaptcha-v3` injects) exposes it on `grecaptcha` itself.
 *
 * @param {Object} [instance] The global grecaptcha instance.
 * @returns {Object|undefined} The api exposing `ready` and `execute`.
 */
const getRecaptchaApi = instance => instance?.enterprise ?? instance;

const useGoogleRecaptcha = () => {
  const { google } = getConfig();
  const isWebView = useIsNativeWebView();
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [intervalId, setIntervalId] = useState(null);
  const [grecaptcha, setGrecaptcha] = useState(null);

  const loadGrecaptcha = () => {
    const { grecaptcha: intervalGrecaptcha } = global ?? {};

    // The script defines the global before the api is usable, so only latch on
    // to it once it can actually be readied.
    if (getRecaptchaApi(intervalGrecaptcha)?.ready && !grecaptcha) {
      setGrecaptcha(intervalGrecaptcha);
    }
  };

  useEffect(() => {
    if (isWebView) {
      return undefined;
    }

    const id = setInterval(loadGrecaptcha, 250);
    setIntervalId(id);

    return () => clearInterval(id);
  }, [isWebView]);

  useEffect(() => {
    const api = getRecaptchaApi(grecaptcha);

    if (!recaptchaLoaded && api?.ready) {
      api.ready(() => {
        setRecaptchaLoaded(true);
      });

      if (intervalId) {
        clearInterval(intervalId);
      }
    }
  }, [grecaptcha]);

  const onTokenRequest = async ({ action }) => {
    let token;
    if (recaptchaLoaded) {
      try {
        token = await getRecaptchaApi(grecaptcha).execute(
          google.recaptchaSiteKey,
          {
            action,
          },
        );
      } catch (e) {
        // A captcha that cannot be executed shouldn't take the action it
        // protects down with it, the platform decides what to do without one.
        token = undefined;
      }
    }

    return token;
  };

  return {
    recaptchaLoaded,
    onTokenRequest,
  };
};

export default useGoogleRecaptcha;
